import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconExclamationCircle, IconGift } from "@tabler/icons-react";
import { DfyApi } from "@/lib/api/dfy-api";
import { dfyQueryKeys, useDfyOrdersQuery } from "@/lib/queries/dfy-queries";
import { paths } from "@/App";
import AppHeader from "@/lib/components/AppHeader";
import { Button } from "@/lib/components/Button/Button";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import NotificationsDropdown from "@/lib/components/NotificationsDropdown";
import { useSidebar } from "@/lib/components/Sidebar";
import Toast from "@/lib/components/Toast";
import DfyOrdersList from "./DfyOrdersList";
import DfyOrdersEmptyState from "./DfyOrdersEmptyState";
import ReferralOverviewDialog from "@/features/Referral/ReferralOverviewDialog";
import { useStartDfyOrderMutation } from "./dfy-order-hooks";
export { dfyStatusLabel } from "./DfyOrdersList";

// Done-for-You customer order list.
export default function DfyOrdersPage() {
  const { teamExternalId } = useParams();
  const queryClient = useQueryClient();
  const { isMobile } = useSidebar();

  const { data, isLoading, isError } = useDfyOrdersQuery(teamExternalId!);

  const createOrderMutation = useStartDfyOrderMutation({
    teamExternalId: teamExternalId!,
    source: "DFY_PAGE",
  });

  const deleteDraftMutation = useMutation({
    mutationFn: (orderExternalId: string) =>
      DfyApi.deleteOrder(teamExternalId!, orderExternalId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: dfyQueryKeys.customerOrders(teamExternalId!),
      });
    },
    onError: () => Toast.error("Failed to delete order"),
  });

  if (!teamExternalId) {
    return null;
  }

  const orders = data?.orders ?? [];
  const showEmptyState = !isLoading && !isError && orders.length === 0;
  const showOrdersHeader = !isLoading && !showEmptyState;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {(isMobile || showOrdersHeader) && (
        <AppHeader
          title={showOrdersHeader ? "Done-for-You" : undefined}
          inlineActions
          actionChildren={
            (!isMobile || orders.length > 0) && (
              <>
                {!isMobile && (
                  <>
                    <NotificationsDropdown />
                    <ReferralOverviewDialog source="DFY_ORDERS">
                      <Button
                        size="sm"
                        variant="tertiarySecondary"
                        icon={<IconGift />}
                        iconPosition="leading"
                      >
                        Earn $100
                      </Button>
                    </ReferralOverviewDialog>
                  </>
                )}
                {orders.length > 0 && (
                  <Button
                    size="sm"
                    isLoading={createOrderMutation.isPending}
                    onClick={() => createOrderMutation.mutate()}
                  >
                    Order videos
                  </Button>
                )}
              </>
            )
          }
          className={showOrdersHeader ? "border-b border-border" : undefined}
        />
      )}

      <div className="flex flex-1 flex-col">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <div
            role="alert"
            className="flex flex-1 items-center justify-center gap-2 py-12"
          >
            <IconExclamationCircle
              size={24}
              className="shrink-0 text-error"
              aria-hidden="true"
            />
            <p className="font-medium">Failed to load orders</p>
          </div>
        ) : showEmptyState ? (
          <DfyOrdersEmptyState
            isCreatingOrder={createOrderMutation.isPending}
            onCreateOrder={() => createOrderMutation.mutate()}
          />
        ) : (
          <DfyOrdersList
            orders={orders}
            orderHref={(order) =>
              order.status === "DRAFT"
                ? paths.dfyOrderForm(teamExternalId, order.externalId)
                : paths.dfyOrder(teamExternalId, order.externalId)
            }
            onDeleteDraft={(externalId) =>
              deleteDraftMutation.mutate(externalId)
            }
          />
        )}
      </div>
    </div>
  );
}
